import React from 'react';
import { Box, Typography } from '@mui/material';

const FONT_CSS = {
    'Times-Roman': '"Times New Roman", Times, serif',
    'Helvetica': 'Helvetica, Arial, sans-serif',
    'Courier': '"Courier New", Courier, monospace',
};

const MOCK = {
    company: 'Plankton AG',
    address: 'Bahnhofstrasse 10, 8001 Zürich',
    phone: '+41 44 123 45 67',
    email: 'info@plankton.ch',
    website: 'www.plankton.ch',
    uid: 'CHE-123.456.789',
    bank: 'CH56 0483 5012 3456 7800 9',
    customer: ['Max Mustermann', 'Musterweg 5', '8050 Zürich'],
    invoiceNo: '2024-001',
    date: '31.05.2026',
    due: '30.06.2026',
    lines: [
        { desc: 'Web Development', qty: 1, rate: "5'000.00", amount: "5'000.00" },
        { desc: 'Hosting Setup',   qty: 1, rate: "800.00",   amount: "800.00"   },
        { desc: 'Support (5h)',    qty: 5, rate: "150.00",   amount: "750.00"   },
    ],
    subtotal: "6'550.00",
    vat: '504.35',
    total: "7'054.35",
};

export default function InvoicePreview({ style, logo }) {
    const color = style?.primaryColor || '#427898';
    const font  = FONT_CSS[style?.fontFamily] || FONT_CSS['Times-Roman'];
    const logoRight = style?.logoPosition === 'RIGHT';

    const footerParts = [
        style?.footerShowAddress && MOCK.address,
        style?.footerShowPhone   && MOCK.phone,
        style?.footerShowEmail   && MOCK.email,
        style?.footerShowWebsite && MOCK.website,
        style?.footerShowUid     && `UID: ${MOCK.uid}`,
        style?.footerShowBank    && `IBAN: ${MOCK.bank}`,
    ].filter(Boolean);

    const txt = (size, weight, col, extra = {}) => ({
        fontSize: size, fontWeight: weight, color: col, fontFamily: font, lineHeight: 1.5, ...extra,
    });

    return (
        <Box>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', mb: 1.5 }}>
                Live Preview
            </Typography>

            <Box sx={{
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                fontFamily: font,
            }}>
                {/* Top color stripe */}
                <Box sx={{ height: 5, backgroundColor: color }} />

                {/* Header: logo + company info */}
                <Box sx={{
                    px: 2.5, py: 2,
                    display: 'flex',
                    flexDirection: logoRight ? 'row-reverse' : 'row',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                    borderBottom: `2px solid ${color}`,
                }}>
                    {logo ? (
                        <Box
                            component="img"
                            src={logo}
                            alt="Company logo"
                            sx={{ maxWidth: 90, maxHeight: 52, flexShrink: 0, objectFit: 'contain' }}
                        />
                    ) : (
                        <Box sx={{
                            width: 52, height: 52, flexShrink: 0,
                            border: `2px dashed ${color}60`,
                            borderRadius: '6px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            backgroundColor: `${color}0d`,
                        }}>
                            <Typography sx={txt(9, 700, `${color}90`)}>LOGO</Typography>
                        </Box>
                    )}
                    <Box sx={{ flex: 1, textAlign: logoRight ? 'left' : 'right' }}>
                        <Typography sx={txt(12, 700, '#1a2332')}>{MOCK.company}</Typography>
                        {style?.headerShowAddress && <Typography sx={txt(9, 400, '#64748b')}>{MOCK.address}</Typography>}
                        {style?.headerShowPhone   && <Typography sx={txt(9, 400, '#64748b')}>{MOCK.phone}</Typography>}
                        {style?.headerShowEmail   && <Typography sx={txt(9, 400, '#64748b')}>{MOCK.email}</Typography>}
                        {style?.headerShowWebsite && <Typography sx={txt(9, 400, '#64748b')}>{MOCK.website}</Typography>}
                    </Box>
                </Box>

                {/* Invoice meta: bill-to + invoice title */}
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #f1f5f9' }}>
                    <Box>
                        <Typography sx={txt(7.5, 700, '#94a3b8', { textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 })}>Bill to</Typography>
                        {MOCK.customer.map((line, i) => (
                            <Typography key={i} sx={txt(9, i === 0 ? 600 : 400, i === 0 ? '#1a2332' : '#64748b')}>{line}</Typography>
                        ))}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={txt(16, 800, color, { letterSpacing: '1px', lineHeight: 1.1 })}>INVOICE</Typography>
                        <Typography sx={txt(9, 400, '#64748b')}>#{MOCK.invoiceNo}</Typography>
                        <Box sx={{ mt: 0.75 }}>
                            <Typography sx={txt(8.5, 400, '#94a3b8')}>
                                Date: <Box component="span" sx={{ color: '#1a2332' }}>{MOCK.date}</Box>
                            </Typography>
                            <Typography sx={txt(8.5, 400, '#94a3b8')}>
                                Due: <Box component="span" sx={{ color: '#1a2332' }}>{MOCK.due}</Box>
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Line items table */}
                <Box sx={{ px: 2.5, pb: 2 }}>
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: '1fr 36px 72px 72px',
                        backgroundColor: color, borderRadius: '4px',
                        px: 1, py: '5px', mt: 1.5, mb: 0.5,
                    }}>
                        {['Description', 'Qty', 'Rate', 'Amount'].map((h, i) => (
                            <Typography key={h} sx={txt(8, 700, '#fff', { textAlign: i === 0 ? 'left' : 'right' })}>{h}</Typography>
                        ))}
                    </Box>
                    {MOCK.lines.map((line, i) => (
                        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 36px 72px 72px', px: 1, py: '4px', borderBottom: '1px solid #f8fafc' }}>
                            <Typography sx={txt(9, 400, '#1a2332')}>{line.desc}</Typography>
                            <Typography sx={txt(9, 400, '#64748b', { textAlign: 'right' })}>{line.qty}</Typography>
                            <Typography sx={txt(9, 400, '#64748b', { textAlign: 'right' })}>{line.rate}</Typography>
                            <Typography sx={txt(9, 500, '#1a2332', { textAlign: 'right' })}>{line.amount}</Typography>
                        </Box>
                    ))}

                    {/* Totals */}
                    <Box sx={{ mt: 1 }}>
                        {[{ label: 'Subtotal', val: `CHF ${MOCK.subtotal}` }, { label: 'VAT 7.7%', val: `CHF ${MOCK.vat}` }].map(r => (
                            <Box key={r.label} sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: '2px', gap: 1 }}>
                                <Typography sx={txt(8.5, 400, '#94a3b8', { minWidth: 58 })}>{r.label}</Typography>
                                <Typography sx={txt(8.5, 400, '#64748b', { minWidth: 72, textAlign: 'right' })}>{r.val}</Typography>
                            </Box>
                        ))}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', backgroundColor: color, borderRadius: '4px', px: 1, py: '5px', mt: 0.5, gap: 1 }}>
                            <Typography sx={txt(9, 700, '#fff', { minWidth: 58 })}>Total</Typography>
                            <Typography sx={txt(9, 700, '#fff', { minWidth: 72, textAlign: 'right' })}>CHF {MOCK.total}</Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Footer */}
                {footerParts.length > 0 ? (
                    <Box sx={{ borderTop: `2px solid ${color}25`, px: 2.5, py: 1, backgroundColor: '#fafbfc' }}>
                        <Typography sx={txt(7.5, 400, '#94a3b8', { textAlign: 'center', lineHeight: 2 })}>
                            {footerParts.join('  ·  ')}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ height: 6, backgroundColor: color, opacity: 0.12 }} />
                )}
            </Box>
        </Box>
    );
}
